import { Link } from "react-router-dom";
import { ArrowRight, Sprout, FlaskConical, Brain, Scale, ShieldCheck, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";

const HERO = "https://images.pexels.com/photos/9350696/pexels-photo-9350696.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const PILLARS = [
  { icon: Scale, label: "Become Thinner", body: "Bitter wild greens reset your fat-burning hormones in a way no supplement can match." },
  { icon: FlaskConical, label: "Free of Toxic Metals", body: "Cilantro mobilizes, chlorella binds — the ancestral 1-2 punch for mercury, lead, and aluminum." },
  { icon: Brain, label: "Cognitively Alive", body: "Lion's Mane, Gotu Kola and Rosemary regrow neural tissue and lift the modern fog of 'meh'." },
  { icon: Sprout, label: "Quietly Healthier", body: "Mineral-rich weeds growing in your backyard rebuild what 50 years of depleted soil and stress have taken." },
];

export default function Landing() {
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    api.get("/plants/preview").then((r) => setPreviews(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="bg-paper">
      {/* Hero */}
      <section className="border-b border-[var(--border-line)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <p className="label-eyebrow">A field guide · est. {new Date().getFullYear()}</p>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight mt-5">
              The weeds at your feet<br />
              <span className="italic text-[var(--forest)]">are the medicine</span><br />
              you've been buying.
            </h1>
            <hr className="divider-rule" />
            <p className="text-lg md:text-xl text-[var(--ink-soft)] leading-relaxed max-w-xl">
              A complete, plain-spoken guide to using wild plants, herbs, and
              the so-called weeds growing around you to lose weight, clear
              heavy metals, sharpen your mind, and feel measurably alive
              again. For <span className="font-mono text-[var(--terracotta)]">$4</span> — once. No subscription. No fluff.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link data-testid="hero-unlock-btn" to="/unlock" className="btn-accent">
                Unlock the Library — $4
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link data-testid="hero-preview-btn" to="/library" className="btn-outline">Preview the herbs</Link>
            </div>

            <p className="mt-6 text-xs text-[var(--muted)] font-mono">
              One-time payment · Lifetime access · Stripe secure checkout
            </p>
          </div>

          <div className="md:col-span-5">
            <div className="frame-border">
              <img
                src={HERO}
                alt="Wild herbs and botanical illustrations"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <p className="font-mono text-xs text-[var(--muted)] mt-3 text-right italic">
              Pl. I — gathered in early summer
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="label-eyebrow">What's inside</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">Four quiet revolutions, growing in plain sight.</h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-10">
            {PILLARS.map((p) => (
              <div key={p.label}>
                <p.icon className="w-6 h-6 text-[var(--forest)]" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl mt-3">{p.label}</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preview grid */}
      <section className="bg-paper-deep border-y border-[var(--border-line)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="label-eyebrow">A glance at the field guide</p>
              <h2 className="font-serif text-4xl md:text-5xl mt-2">From the library.</h2>
            </div>
            <Link data-testid="preview-see-all-btn" to="/library" className="btn-ghost text-sm">See all plants →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {previews.map((p) => (
              <article key={p.slug} className="card-journal" data-testid={`preview-card-${p.slug}`}>
                <div className="aspect-[4/3] overflow-hidden mb-4 frame-border p-0">
                  <img src={p.image_url} alt={p.common_name} className="w-full h-full object-cover" />
                </div>
                <p className="font-mono text-xs text-[var(--sage)] italic">{p.scientific_name}</p>
                <h3 className="font-serif text-2xl mt-1">{p.common_name}</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed line-clamp-3">{p.summary}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pitch */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
        <Quote className="w-7 h-7 text-[var(--terracotta)] mx-auto" strokeWidth={1.5} />
        <p className="font-serif text-3xl md:text-4xl mt-5 leading-tight italic">
          "Most of what you need is already growing in the cracks of the sidewalk. The question isn't whether it works. It's whether you have the field guide."
        </p>
        <hr className="divider-rule mx-auto" />
        <p className="text-[var(--ink-soft)] text-base">
          That field guide — every plant, every recipe, every caution — is
          inside the library. <strong className="font-semibold text-[var(--ink)]">For $4. Forever.</strong>
        </p>

        <div className="mt-10 flex flex-col items-center gap-4">
          <Link data-testid="bottom-unlock-cta" to="/unlock" className="btn-accent">Unlock the Library — $4</Link>
          <p className="text-xs text-[var(--muted)] font-mono flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> One-time payment · Lifetime access</p>
        </div>
      </section>
    </div>
  );
}
